import { Controller } from "@hotwired/stimulus"
import { apiGet, apiPost, apiDelete } from "../shared/api"
import { escapeHtml, loadTranslations, t, formatShortDate } from "../shared/utils"

export default class extends Controller {
  static targets = ["content"]
  static values = {
    newAnnouncementPath: String,
    newListingPath: String,
    searchPath: String,
    marketplacePath: String
  }

  connect() {
    loadTranslations()
    this._fetch()
  }

  async _fetch() {
    const data = await apiGet("/api/v1/me/announcements")
    if (!data) return

    const d = data.data || data
    this.contentTarget.innerHTML = this._renderPartnerSection(d.partner_announcements) + this._renderMarketplaceSection(d.marketplace_listings)
  }

  _renderPartnerSection(announcements) {
    const header = `
      <div class="mb-5">
        <div class="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <h4 class="mb-0"><a href="${this.searchPathValue}" class="mine-section-title-link">${t("partner_announcements.mine.seeking_partner")}</a></h4>
          <a href="${this.newAnnouncementPathValue}" class="default-btn cta-primary"><i class="fa-solid fa-plus me-2"></i>${t("nav.create_announcement")}</a>
        </div>`

    if (!announcements?.length) {
      return header + `
        <div class="card"><div class="card-body text-center py-5">
          <i class="fa-solid fa-bullhorn fa-3x text-muted mb-3"></i>
          <p class="text-muted">${t("partner_announcements.mine.no_partner_announcements")}</p>
        </div></div></div>`
    }

    const rows = announcements.map(a => this._announcementRow(a)).join("")
    return header + `
      <div class="card"><div class="card-body p-0"><div class="table-responsive">
        <table class="table table-hover mb-0">
          <thead><tr>
            <th>${t("common.title")}</th>
            <th>${t("common.looking_for")}</th>
            <th>${t("common.status")}</th>
            <th>${t("common.created_at")}</th>
            <th class="text-end">${t("common.actions")}</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div></div></div></div>`
  }

  _announcementRow(a) {
    const sexLabel = a.looking_for_sex
      ? (a.looking_for_sex === "male" ? t("enums.user.sex.male") : t("enums.user.sex.female"))
      : "-"

    let statusBadge
    if (a.published || a.active) {
      statusBadge = `<span class="badge bg-success">${t("enums.partner_announcement.status.active")}</span>`
    } else if (a.expired) {
      statusBadge = `<span class="badge bg-danger">${t("enums.partner_announcement.status.expired", "Wygasło")}</span>`
    } else {
      statusBadge = `<span class="badge bg-warning text-dark">${t(`enums.partner_announcement.status.${a.status}`, a.status)}</span>`
    }

    let actions = `
      <a href="/partner_announcements/${a.id}" class="btn btn-sm btn-outline-secondary me-1" title="${t("common.show")}"><i class="fa-solid fa-eye"></i></a>
      <a href="/partner_announcements/${a.id}/edit" class="btn btn-sm btn-outline-secondary me-1" title="${t("common.edit")}"><i class="fa-solid fa-pen"></i></a>`

    if (a.draft) {
      actions += `<button class="btn btn-sm btn-mine-publish me-1" data-action="click->my-announcements#publishAnnouncement" data-id="${a.id}">${t("marketplace.publish")}</button>`
    } else if (a.active) {
      actions += `<button class="btn btn-sm btn-outline-secondary me-1" data-action="click->my-announcements#closeAnnouncement" data-id="${a.id}">${t("marketplace.close", "Zamknij")}</button>`
    } else if (a.closed || a.expired) {
      actions += `<button class="btn btn-sm btn-mine-publish me-1" data-action="click->my-announcements#reopenAnnouncement" data-id="${a.id}">${t("partner_announcements.reopen")}</button>`
    }
    actions += `<button class="btn btn-sm btn-outline-danger" data-action="click->my-announcements#deleteAnnouncement" data-id="${a.id}"><i class="fa-solid fa-trash"></i></button>`

    return `<tr>
      <td><a href="/partner_announcements/${a.id}">${escapeHtml(a.title)}</a></td>
      <td>${sexLabel}</td>
      <td>${statusBadge}</td>
      <td>${formatShortDate(a.created_at)}</td>
      <td class="text-end">${actions}</td>
    </tr>`
  }

  _renderMarketplaceSection(listings) {
    const header = `
      <div class="mb-5">
        <div class="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <h4 class="mb-0"><a href="${this.marketplacePathValue}" class="mine-section-title-link">${t("partner_announcements.mine.marketplace_listings")}</a></h4>
          <a href="${this.newListingPathValue}" class="default-btn cta-primary"><i class="fa-solid fa-plus me-2"></i>${t("marketplace.add_listing")}</a>
        </div>`

    if (!listings?.length) {
      return header + `
        <div class="card"><div class="card-body text-center py-5">
          <i class="fa-solid fa-box-open fa-3x text-muted mb-3"></i>
          <p class="text-muted">${t("partner_announcements.mine.no_marketplace_listings")}</p>
        </div></div></div>`
    }

    const rows = listings.map(l => this._listingRow(l)).join("")
    return header + `
      <div class="card"><div class="card-body p-0"><div class="table-responsive">
        <table class="table table-hover mb-0">
          <thead><tr>
            <th>${t("common.title")}</th>
            <th>${t("common.category")}</th>
            <th>${t("common.status")}</th>
            <th>${t("common.created_at")}</th>
            <th class="text-end">${t("common.actions")}</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div></div></div></div>`
  }

  _listingRow(l) {
    let statusBadge
    if (l.active) {
      statusBadge = `<span class="badge bg-success">${t("enums.marketplace_listing.status.active")}</span>`
    } else if (l.expired) {
      statusBadge = `<span class="badge bg-danger">${t("enums.marketplace_listing.status.expired", "Wygasło")}</span>`
    } else {
      statusBadge = `<span class="badge bg-warning text-dark">${t(`enums.marketplace_listing.status.${l.status}`, l.status)}</span>`
    }

    const category = t(`marketplace.categories.${l.category}`, l.category)

    let actions = `
      <a href="/marketplace/${l.id}" class="btn btn-sm btn-outline-secondary me-1" title="${t("common.show")}"><i class="fa-solid fa-eye"></i></a>
      <a href="/marketplace/${l.id}/edit" class="btn btn-sm btn-outline-secondary me-1" title="${t("common.edit")}"><i class="fa-solid fa-pen"></i></a>`

    if (l.draft) {
      actions += `<button class="btn btn-sm btn-mine-publish me-1" data-action="click->my-announcements#publishListing" data-id="${l.id}">${t("marketplace.publish")}</button>`
    }
    if (l.active && l.seller_has_extended_duration) {
      actions += `<button class="btn btn-sm btn-outline-secondary me-1" data-action="click->my-announcements#renewListing" data-id="${l.id}">${t("marketplace.renew")}</button>`
    }
    if (l.active) {
      actions += `<button class="btn btn-sm btn-outline-secondary me-1" data-action="click->my-announcements#closeListing" data-id="${l.id}">${t("marketplace.close", "Zamknij")}</button>`
    }
    actions += `<button class="btn btn-sm btn-outline-danger" data-action="click->my-announcements#deleteListing" data-id="${l.id}"><i class="fa-solid fa-trash"></i></button>`

    return `<tr>
      <td><a href="/marketplace/${l.id}">${escapeHtml(l.title)}</a></td>
      <td>${category}</td>
      <td>${statusBadge}</td>
      <td>${formatShortDate(l.created_at)}</td>
      <td class="text-end">${actions}</td>
    </tr>`
  }

  async publishAnnouncement(e) {
    const id = e.currentTarget.dataset.id
    await apiPost(`/api/v1/partner_announcements/${id}/publish`)
    this._fetch()
  }

  async closeAnnouncement(e) {
    const id = e.currentTarget.dataset.id
    await apiPost(`/api/v1/partner_announcements/${id}/close`)
    this._fetch()
  }

  async reopenAnnouncement(e) {
    const id = e.currentTarget.dataset.id
    await apiPost(`/api/v1/partner_announcements/${id}/reopen`)
    this._fetch()
  }

  async deleteAnnouncement(e) {
    if (!confirm(t("common.confirm_delete"))) return
    const id = e.currentTarget.dataset.id
    await apiDelete(`/api/v1/partner_announcements/${id}`)
    this._fetch()
  }

  async publishListing(e) {
    const id = e.currentTarget.dataset.id
    await apiPost(`/api/v1/marketplace/${id}/publish`)
    this._fetch()
  }

  async renewListing(e) {
    const id = e.currentTarget.dataset.id
    await apiPost(`/api/v1/marketplace/${id}/renew`)
    this._fetch()
  }

  async closeListing(e) {
    const id = e.currentTarget.dataset.id
    await apiPost(`/api/v1/marketplace/${id}/close`)
    this._fetch()
  }

  async deleteListing(e) {
    if (!confirm(t("common.confirm_delete"))) return
    const id = e.currentTarget.dataset.id
    await apiDelete(`/api/v1/marketplace/${id}`)
    this._fetch()
  }
}
